import { pickMultipleItemsContent, pickSingleItemContent } from "@eusoft/webapp-framework/content/Pickers";
import { showContentPopup } from "@eusoft/webapp-framework/helpers/Editor";
import { userInteraction } from "@eusoft/webapp-framework/services/UserInteraction";
import { forModel } from "@eusoft/webapp-jsx";
import { Action, ArrayEditor, arrayItemsSource, Content, contentInfo, formatCurrency, formatString, formatText, MaterialIcon } from "@eusoft/webapp-ui";
import { CatalogProductStatus, type Guid, type ICatalogGroupUpdate, type ICatalogGroupView, type ICatalogProductUpdate, type IProductFormatView, type IProductListView } from "../../entities/Commands";
import { entityItemsSource } from "../../helpers/ItemsSources";
import { Authorize } from "../../services/Authorize";
import { executeAsync } from "../../services/DataService";
import { WithContext } from "../../services/WithContext";
import "./EditFullCatalogPage.scss";
import { registerComponent } from "@eusoft/webapp-core/Component";

export interface IEditFullCatalogArgs {
    catalogId: Guid;
}

interface INavEntry {
    group: ICatalogEditGroup;
    name?: string;
}

interface ICatalogEditGroup {
    name: string;
    isHidden: string;
    icon: string;
    products: ICatalogEditProduct[];
    groups: ICatalogEditGroup[];
}

interface ICatalogEditFormat {
    id: Guid;
    name: string;
    price: number;
}
interface ICatalogEditProduct {
    id: Guid;
    name: string;
    status: CatalogProductStatus;
    formats: ICatalogEditFormat[];
}


export function productView(item: ICatalogEditProduct) {

    return <>
        <div className="name"> {item.name}</div>
        <div className="formats">
            {item.formats.forEach(a => <>
                <span>{a.name}</span>
                <span>{formatCurrency(a.price)}</span>
            </>)}
        </div>
    </>
}

export class EditFullCatalogPage extends Content<IEditFullCatalogArgs> {

    protected _root: ICatalogEditGroup;
    protected _merchantId: Guid;

    constructor() {

        super();


        this.init(EditFullCatalogPage, {
            style: ["panel"],       
            title: formatText("edit-catalog"),
            body: forModel(this, m => <>
                {m.nav.forEach(a => <nav>
                    <Action style="text">{a.name ?? a.group.name}</Action>
                </nav>)}
                <h3>{formatText("products")}</h3>
                <ArrayEditor
                    newItemLabel="add-product"
                    editMode="picker"
                    name="products"
                    canEdit={false}
                    style={["round-item"]}
                    pickItemAsync={() => m.pickProductAsync()}
                    value={m.activeGroup?.products}
                    itemView={a => productView(a)}      
                />
            </>),
            actions: [{
                name: "save",
                text: "save",
                executeAsync: () => this.saveAsync() 
            }]
        });
    }


    override async onLoadAsync(args: IEditFullCatalogArgs) {

        this.catalogId = args.catalogId;

        const data = await executeAsync("GetFullCatalog", {
            id: args.catalogId
        });

        this.title = formatString("edit-item", data.name);

        const visit = (view: ICatalogGroupView, edit: ICatalogEditGroup) => {
            edit.name = view.name;
            edit.products = [];
            edit.groups = [];

            if (view.groups) {

                for (const viewGrp of view.groups) {
                    const editGrp = {} as ICatalogEditGroup;
                    visit(viewGrp, editGrp);
                    edit.groups.push(editGrp);
                }
            }

            if (view.products) {

                for (const viewProduct of view.products) {
                    const editProduct = {
                        id: viewProduct.id,
                        name: viewProduct.name,
                        status: viewProduct.status,
                        formats: viewProduct.formats.map(a => ({
                            id: a.id,
                            name: a.name,
                            price: a.price
                        } as ICatalogEditFormat))
                    } as ICatalogEditProduct;
                    edit.products.push(editProduct);
                }
            }
        }

        this._root = {} as ICatalogEditGroup;
        this._merchantId = data.merchantId;

        visit(data.root, this._root);

        this.nav.push({
            group: this._root,
            name: "root"
        })

        this.activeGroup = this._root;

        return true;
    }

    async saveAsync() {
        const visit = (update: ICatalogGroupUpdate, edit: ICatalogEditGroup) => {
            update.name = edit.name;
            update.products = [];
            update.groups = [];

            if (edit.groups) {

                for (const editGrp of edit.groups) {
                    const updateGrp = {} as ICatalogGroupUpdate;
                    visit(updateGrp, editGrp);
                    update.groups.push(updateGrp);
                }
            }

            if (edit.products) {

                for (const editProduct of edit.products) {

                    for (const format of editProduct.formats) {
                        const updateProduct = {} as ICatalogProductUpdate;
                        updateProduct.productId = editProduct.id;
                        updateProduct.formatId = format.id;
                        updateProduct.status = editProduct.status;
                        update.products.push(updateProduct);
                    }
       
                }
            }
        }

        const updateRoot = {} as ICatalogGroupUpdate;

        visit(updateRoot, this._root);

        const res = await executeAsync("UpdateFullCatalog", {
            root: updateRoot,
            id: this.catalogId,
            merchantId: this._merchantId
        });

        userInteraction.messageAsync(formatText("msg-catalog-saved"), "success", 4000);
    }


    protected getAddedProductsIds() {

        const result: Guid[] = [];

        const visit = (grp: ICatalogEditGroup) => {

            if (grp.groups) {
                for (const innerGrp of grp.groups)
                    visit(innerGrp)
            }

            if (grp.products)
                result.push(...grp.products.map(a => a.id));
        }

        visit(this._root);

        return result;
    }

    async pickProductAsync(): Promise<ICatalogEditProduct> {

        const content = pickSingleItemContent({
            itemsSource: entityItemsSource("ListProduct"),
            prepareFilter: (filter, offset, limit) => ({
                ...filter,
                merchantId: this._merchantId,
                excludeIds: this.getAddedProductsIds()
            }),
            queryField: "query",
            title: "select-product",
            pageSize: 50, 
        });

        const product: IProductListView = await showContentPopup(content, undefined, {
            closeLabel: "cancel"
        });

        if (!product)
            return;

        let formats: IProductFormatView[];

        if (product.formats.length > 1) {
            const formatContent = pickMultipleItemsContent({
                itemsSource: {
                    ...arrayItemsSource(product.formats, a => a.name, a => a.id),
                },
                title: "select-formats",
                showSelectAll: true,
                validate: items => {
                    if (items.length == 0)
                        return false;
                    return true;
                }
            });

            formats = await showContentPopup(formatContent, undefined, {
                closeLabel: "cancel"
            });

            if (!formats)
                return;
        }
        else
            formats = product.formats;

        return {
            id: product.id,
            name: product.name,
            status: CatalogProductStatus.None,
            formats: formats.map(a => ({
                id: a.id,
                name: a.name,
                price: a.price
            }))
        };
    }

    nav: INavEntry[] = [];

    activeGroup: ICatalogEditGroup;

    catalogId: Guid;

    static override info = contentInfo({
        name: "edit-full-catalog",
        route: "/catalog/{catalogId}/edit",
        factory: () => new EditFullCatalogPage(),
        icon: <MaterialIcon style="red" name="arrow_circle_down" />,
        features: [
            Authorize<IEditFullCatalogArgs>(bld =>
                bld.businessAccount()
                    .resource("ProductCatalog", "edit", bld.args.catalogId)),
            WithContext("sale-circuit")
        ]
    })
}

registerComponent(EditFullCatalogPage, "EditFullCatalogPage");